import { Popover, PopoverDropdown } from '@mantine/core';
import { useUnit } from 'effector-react';
import { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { gameModel } from 'entities/game';
import { ActionIcon, Button, NumberFormatter } from 'shared/ui';
import CloseIcon from 'shared/ui/assets/icons/cross-icon.svg?react';
import { INTERACTIVE_ELEMENT_MESSAGES } from '../../config';
import { interactiveElementModel } from '../../model';
import cls from './interactive-element-message.module.scss';

type InteractiveElementMessageProps = {
	onClose?: () => void;
} & PropsWithChildren;

export const InteractiveElementMessage = ({
	onClose,
	children,
}: InteractiveElementMessageProps) => {
	const { t } = useTranslation();

	const [opened, variant, profit, currency, pending, close, playAgain] = useUnit([
		interactiveElementModel.messageSwitcher.$active,
		interactiveElementModel.$messageVariant,
		interactiveElementModel.$profit,
		gameModel.$currency,
		gameModel.$sendBetPending,
		interactiveElementModel.messageSwitcher.disable,
		interactiveElementModel.playAgain,
	]);

	const formattedProfit = (
		<NumberFormatter
			fixedDecimalScale
			value={profit}
			suffix={` ${currency}`}
			thousandSeparator=' '
			decimalScale={2}
		/>
	);

	return (
		<Popover
			opened={opened}
			position='bottom'
			onClose={() => {
				close();
				onClose?.();
			}}
		>
			<Popover.Target>{children}</Popover.Target>
			<PopoverDropdown>
				<ActionIcon
					variant='standard-inverted'
					className={cls.interactiveElementMessageClose}
					onClick={() => {
						close();
						onClose?.();
					}}
				>
					<CloseIcon />
				</ActionIcon>
				<div className={cls.interactiveElementMessageInner}>
					{variant === INTERACTIVE_ELEMENT_MESSAGES.luckNextTime && (
						<p>{t('interactive-element.luck-next-time')}!</p>
					)}
					{variant === INTERACTIVE_ELEMENT_MESSAGES.goodGame && (
						<p>
							<span>{t('interactive-element.good-game')}!</span> <br />
							<span>
								{t('interactive-element.you-win')}: {formattedProfit}!
							</span>
						</p>
					)}
					{variant === INTERACTIVE_ELEMENT_MESSAGES.skillIncredible && (
						<p className={cls.interactiveElementMessageParagraph}>
							<span>{t('interactive-element.you-win')}!</span> <br />
							<span data-small>{t('interactive-element.skill-incredible')}!</span>
							<br />
							<span data-small>
								{t('interactive-element.you-win')}: {formattedProfit}!
							</span>
						</p>
					)}
					<Button
						variant='secondary'
						disabled={pending || !opened}
						onClick={playAgain}
					>
						{t('interactive-element.play-again')}!
					</Button>
				</div>
			</PopoverDropdown>
		</Popover>
	);
};
